import { useRef } from "react";
import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform,
} from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Crown, ShieldCheck } from "lucide-react";

import HeroShowcase from "./HeroShowcase";
import ProjectCard from "./ProjectCard";
import { Reveal, RevealGroup, RevealItem } from "./Reveal";
import ScrollProgress from "./ScrollProgress";
import { SCREENS, PhoneMockup } from "./screens";

const PLAY_URL =
  "https://play.google.com/store/apps/details?id=com.rovenkodev.FitnessGuru";
// The "rovenkodev" link in the navbar and footer points at the Play developer
// profile, which lists every app we ship from one place.
const DEV_PLAY_URL = "https://play.google.com/store/apps/dev?id=6638725637924776409";

// Mirrors the in-app paywall (components/PaywallOverlay.js -> PLACEHOLDER).
// purchasesStore.js still points at RevenueCat Test Store product ids, so these
// are advertised as "from" prices and Google Play always shows the real one.
// Array order is the display order: Monthly first, then the Yearly plan, which
// still carries the gold frame, glow and savings ribbon as the recommended one.
const PRO_PLANS = [
  {
    key: "monthly",
    name: "Monthly",
    price: "$7.99",
    unit: "/month",
    caption: "billed monthly",
    badge: null,
    primary: false,
  },
  {
    key: "annual",
    name: "Yearly",
    price: "$47.99",
    unit: "/year",
    caption: "billed yearly",
    badge: "SAVE 50%",
    primary: true,
  },
];

// Free vs Pro. Kept in sync with the app's entitlement gates: the core program,
// progress tracking, demos and reminders are free, and Pro unlocks the rest.
// Custom-plan limits come from workoutPrograms.js (FREE=1, PRO=3).
const PRO_COMPARISON = [
  { label: "Workout programs", free: "Core program", pro: "All 11 unlocked" },
  { label: "Custom training plans", free: "1 plan", pro: "3 plans" },
  { label: "Progress, streaks & trophies", free: true, pro: true },
  { label: "Exercise demos & technique tips", free: true, pro: true },
  { label: "Reminders", free: true, pro: true },
  { label: "No ads", free: true, pro: true },
];

const PRO_INCLUDES = [
  "Every program and every difficulty level",
  "Up to 3 custom training plans you build yourself",
  "Full progress tracking, streaks and statistics",
  "Exercise demos, technique tips and guides",
  "Cancel or change your plan any time in Google Play",
];

// Quality claims for the "built by athletes and professionals" section. Kept
// deliberately general - they restate the same promises the in-app content makes
// (demonstrations, technique notes, real sets/reps/rest) rather than asserting
// credentials we cannot point at.
const CRAFTED_POINTS = [
  "Every exercise is demonstrated and reviewed before it reaches the app",
  "Sets, reps and rest follow real hypertrophy programming",
  "Technique notes focus on the mistakes that actually cost you reps",
  "Each program and difficulty level is tested through before it ships",
];

// A tick for the boolean rows, the raw value otherwise - the same check/label
// treatment the in-app paywall uses for its plan rows.
const ComparisonValue = ({ value, pro }) => {
  if (value === true) {
    return (
      <span
        className={`inline-flex h-6 w-6 items-center justify-center rounded-full ${
          pro ? "bg-accent/15 text-accent" : "bg-white/5 text-ink-secondary"
        }`}
      >
        <Check size={14} />
      </span>
    );
  }
  return (
    <span className={pro ? "font-black text-accent" : "text-ink-secondary"}>
      {value}
    </span>
  );
};

const SectionHeading = ({ eyebrow, title, copy }) => (
  <div className="mx-auto max-w-2xl text-center">
    {eyebrow ? (
      <span className="text-[11px] font-black uppercase tracking-[0.25em] text-accent">
        {eyebrow}
      </span>
    ) : null}
    <h2 className="mt-3 font-bebas text-4xl tracking-wider text-ink-primary sm:text-5xl">
      {title}
    </h2>
    {copy ? <p className="mt-4 text-ink-secondary">{copy}</p> : null}
  </div>
);

function DayFitness() {
  // Scroll-linked hero exit. This is the one part of the page that responds to
  // where the reader actually is; everything below it is a one-shot reveal, so
  // without this the gap between the video loop and the first section is dead
  // air.
  //
  // The copy is driven from its PARENT wrapper, not from the motion.div that
  // carries the load-in. That node already owns `y`, and two transforms on one
  // element fight each other. Nesting them composes cleanly, and it leaves the
  // load-in - and therefore the LCP path HeroShowcase works hard to protect -
  // exactly as it was.
  const heroRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const { scrollYProgress: heroProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroCopyY = useTransform(heroProgress, [0, 1], [0, 110]);
  const heroCopyFade = useTransform(heroProgress, [0, 0.75], [1, 0]);
  // Scales up rather than down, so the section's own overflow-hidden crops the
  // media instead of opening a gap at the edges.
  const heroMediaScale = useTransform(heroProgress, [0, 1], [1, 1.06]);
  const heroMediaFade = useTransform(heroProgress, [0, 0.9], [1, 0.6]);

  return (
    <div className="flex min-h-screen flex-col bg-app-bg font-inter text-ink-primary">
      {/* Header */}
      {/* Header. A plain element, not a motion one: App fades the page in on
          navigation, and a header that also slid in on mount made the two
          animations read as a stutter. */}
      <header className="sticky top-0 z-30 flex w-full items-center justify-between border-b border-white/10 bg-app-bg px-5 py-5 sm:px-8">
        <Link to="/" className="flex items-center gap-3">
          <span className="font-oswald text-2xl font-semibold tracking-wide sm:text-3xl">
            30 DAY FITNESS
          </span>
        </Link>

        <div className="flex items-center gap-4 sm:gap-6">
          <a
            href="#pro"
            // Outlined rather than a solid gold fill on purpose: bg-accent is
            // the site's reserved primary-CTA treatment, and spending it on the
            // chrome made a 12px pill compete with the real "Get it on Google
            // Play" buttons further down the page. This reads as a badge that
            // still says premium, and the glow is what carries it at this size.
            className="group inline-flex shrink-0 items-center gap-2 rounded-full border border-accent/40 px-3 py-1.5 text-xs font-black uppercase tracking-[0.18em] text-accent shadow-gold transition hover:border-accent hover:bg-accent/10 hover:shadow-gold-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 sm:px-4 sm:py-2"
          >
            <Crown
              size={14}
              className="shrink-0 transition-transform duration-300 group-hover:scale-110"
            />
            <span className="hidden sm:inline">30 Day Fitness </span>Pro
          </a>
          <a
            href={DEV_PLAY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[11px] font-semibold uppercase tracking-[0.25em] text-ink-tertiary transition hover:text-ink-primary"
          >
            <span className="text-accent">rovenko</span>dev
          </a>
        </div>

        <ScrollProgress />
      </header>

      <main className="flex-grow">
        {/* Hero. The five alpha clips are the full-bleed background of the whole
            section rather than a panel beside the copy, so the headline sits
            over them behind a scrim that keeps its contrast. */}
        <section
          ref={heroRef}
          className="relative isolate overflow-hidden"
        >
          {/* -z-20 keeps the clips at the floor of the section's stacking context,
              the scrim above them at -z-10, and the copy above that. On wide
              screens the device is also slid right so it sits beside the copy
              instead of behind the headline; the alpha clips leave their own
              margins transparent, so the shift opens no visible gap. That starts
              at lg, not md: between 768 and 1024 the copy still spans most of the
              column and would collide with the device. */}
          {/* Wrapper only, so the scroll transform lands on a node without an
              overflow-hidden of its own - HeroShowcase's root clips, and a
              scale on that element would be cropped by its own container. */}
          <motion.div
            className="absolute inset-0 -z-20"
            style={
              reduceMotion
                ? undefined
                : { scale: heroMediaScale, opacity: heroMediaFade }
            }
          >
            <HeroShowcase className="lg:translate-x-[22%]" />
          </motion.div>

          {/* Scrim over the clips, between them and the copy. Three tiers, because
              how bright the device reads depends on how much of the frame the
              copy covers. On phones the device is cropped tight and can stay
              fairly visible. In the md range the hero is nearly square, so the
              device fills the frame behind the copy and has to be held right
              down. From lg up the copy is beside the device, so the scrim can
              hold near-opaque across the text column and then fall away,
              leaving the device crisp where nothing else competes. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-b from-app-bg/95 via-app-bg/75 to-app-bg/60 md:from-app-bg md:via-app-bg/90 md:to-app-bg/85 lg:bg-gradient-to-r lg:from-app-bg lg:via-app-bg/80 lg:to-app-bg/20"
          />

          {/* A second fade, bottom edge only, so the device dissolves into the
              next section rather than being cut off square by the hero's end.
              Side-by-side only: where the copy runs to the bottom of the section
              the fade would swallow the device for no benefit. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 bottom-0 -z-10 hidden h-40 bg-gradient-to-t from-app-bg to-transparent lg:block"
          />

          <motion.div
            style={
              reduceMotion ? undefined : { y: heroCopyY, opacity: heroCopyFade }
            }
            className="relative mx-auto max-w-6xl px-5 pb-20 pt-20 sm:px-8 md:pb-32 md:pt-32"
          >
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-2xl text-center md:text-left"
            >
              <div>
                <span className="text-[11px] font-black uppercase tracking-[0.25em] text-accent">
                  Gym-focused 30-day plans
                </span>

                <h1 className="text-bebas-heavy mt-4 font-bebas text-7xl leading-[0.9] tracking-wider sm:text-8xl md:text-9xl">
                  <span className="block text-ink-primary">30 DAY</span>
                  <span className="block text-accent">FITNESS</span>
                </h1>

                <p className="mt-6 max-w-lg text-lg leading-relaxed text-ink-secondary">
                  Your ultimate gym companion. Structured workout programs,
                  exercise demonstrations and progress tracking that carry you
                  from day one to day thirty.
                </p>

                <div className="mt-8 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
                  <a
                    href={PLAY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-accent px-8 py-4 text-sm font-black uppercase tracking-wider text-app-bg shadow-gold transition hover:brightness-110"
                  >
                    Get it on Google Play
                    <ArrowRight size={16} />
                  </a>
                  <a
                    href="#pro"
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-white/10 bg-app-surface px-8 py-4 text-sm font-bold uppercase tracking-wider text-ink-primary transition hover:border-accent/40 hover:text-accent"
                  >
                    <Crown size={15} />
                    See what&rsquo;s in Pro
                  </a>
                </div>

                <dl className="mt-10 flex items-center justify-center gap-8 md:justify-start">
                  {[
                    ["30", "days"],
                    ["0", "accounts"],
                  ].map(([stat, label]) => (
                    <div key={label}>
                      <dt className="font-bebas text-3xl text-accent">
                        {stat}
                      </dt>
                      <dd className="text-[10px] font-bold uppercase tracking-[0.2em] text-ink-tertiary">
                        {label}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            </motion.div>
          </motion.div>
        </section>

        {/* 30 Day Fitness Pro - mirrors the in-app paywall (PaywallOverlay.js):
            the same two plans, the same savings ribbon and the same free/Pro
            split the app gates content behind. */}
        <section id="pro" className="relative scroll-mt-24 px-5 sm:px-8">
          <div className="mx-auto max-w-6xl rounded-3xl border border-white/10 bg-app-surface p-6 sm:p-10 md:p-14">
            <Reveal>
              <SectionHeading
                eyebrow="In-app subscription"
                title="Unlock 30 Day Fitness"
                copy="Every program, every day. Cancel anytime."
              />
            </Reveal>

            {/* Plan cards. The yearly plan carries the gold frame + glow and the
                savings ribbon, exactly like the app's primary plan card. The two
                cards stagger in as the row scrolls into view. */}
            <RevealGroup className="mx-auto mt-12 grid max-w-3xl gap-5 md:grid-cols-2">
              {PRO_PLANS.map((plan) => (
                <RevealItem key={plan.key} className="relative">
                  {plan.badge ? (
                    <span className="absolute -top-3 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-full border-2 border-app-surface bg-accent px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-app-bg">
                      {plan.badge}
                    </span>
                  ) : null}

                  <a
                    href={PLAY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${plan.name} plan, ${plan.price} ${plan.unit} ${plan.caption}`}
                    className={`flex min-h-[140px] flex-col justify-center rounded-2xl border px-6 py-7 transition motion-safe:hover:-translate-y-1 hover:brightness-110 ${
                      plan.primary
                        ? "border-accent bg-app-raised shadow-gold motion-safe:hover:shadow-gold-lg"
                        : "border-white/10 bg-app-bg hover:border-accent/40"
                    }`}
                  >
                    <span className="text-[11px] font-black uppercase tracking-[0.2em] text-ink-tertiary">
                      {plan.name}
                    </span>
                    <span className="mt-2 flex items-baseline gap-1">
                      <span className="font-bebas text-5xl leading-none text-ink-primary">
                        {plan.price}
                      </span>
                      <span className="text-sm font-bold text-ink-secondary">
                        {plan.unit}
                      </span>
                    </span>
                    <span className="mt-2 text-xs font-semibold text-ink-tertiary">
                      {plan.caption}
                    </span>
                  </a>
                </RevealItem>
              ))}
            </RevealGroup>

            <Reveal
              as="p"
              className="mt-6 text-center text-xs text-ink-tertiary"
            >
              Starting prices shown - Google Play displays the final price at
              checkout.
            </Reveal>

            {/* Free vs Pro. The header and every row rise in one after the
                other as the table scrolls into view. */}
            <RevealGroup className="mx-auto mt-12 max-w-3xl overflow-hidden rounded-2xl border border-white/10 bg-app-bg">
              <RevealItem className="grid grid-cols-[1fr_5rem_6rem] items-center gap-2 border-b border-white/10 bg-app-raised px-5 py-3.5 sm:grid-cols-[1fr_7rem_8rem] sm:px-6">
                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-ink-tertiary">
                  What&rsquo;s included
                </span>
                <span className="text-center text-[10px] font-black uppercase tracking-[0.2em] text-ink-tertiary">
                  Free
                </span>
                <span className="text-center text-[10px] font-black uppercase tracking-[0.2em] text-accent">
                  Pro
                </span>
              </RevealItem>

              {PRO_COMPARISON.map((row) => (
                <RevealItem
                  key={row.label}
                  className="grid grid-cols-[1fr_5rem_6rem] items-center gap-2 border-b border-white/5 px-5 py-4 last:border-b-0 sm:grid-cols-[1fr_7rem_8rem] sm:px-6"
                >
                  <span className="text-sm text-ink-secondary">
                    {row.label}
                  </span>
                  <span className="flex justify-center">
                    <ComparisonValue value={row.free} />
                  </span>
                  <span className="flex justify-center">
                    <ComparisonValue value={row.pro} pro />
                  </span>
                </RevealItem>
              ))}
            </RevealGroup>

            <RevealGroup
              as="ul"
              className="mx-auto mt-10 grid max-w-3xl gap-3 sm:grid-cols-2"
            >
              {PRO_INCLUDES.map((item) => (
                <RevealItem
                  as="li"
                  key={item}
                  className="flex items-start gap-3 text-sm text-ink-secondary"
                >
                  <Check size={16} className="mt-0.5 shrink-0 text-accent" />
                  {item}
                </RevealItem>
              ))}
            </RevealGroup>

            <Reveal
              as="p"
              className="mx-auto mt-8 flex max-w-3xl items-start justify-center gap-2 text-center text-xs leading-relaxed text-ink-tertiary"
            >
              <ShieldCheck size={15} className="mt-0.5 shrink-0 text-accent" />
              <span>
                Cancel anytime in the App Store or Google Play. Subscriptions
                renew automatically until cancelled, and can be restored any
                time with &ldquo;Restore Purchases&rdquo; in the app.
              </span>
            </Reveal>
          </div>
        </section>

        {/* Download & App Preview */}
        <Reveal
          as="section"
          className="mx-auto mt-24 grid w-full max-w-6xl items-center gap-12 px-5 sm:px-8 md:grid-cols-2 md:gap-16"
        >
          <div>
            <SectionHeading
              eyebrow="Google Play"
              title="Download the App Now"
              copy="Start your transformation today with 30 Day Fitness. Available now on Google Play!"
            />

            <div className="mt-8 flex justify-center">
              <a
                href={PLAY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-accent px-8 py-4 text-sm font-black uppercase tracking-wider text-app-bg shadow-gold transition hover:brightness-110"
              >
                Get it on Google Play
                <ArrowRight size={16} />
              </a>
            </div>

            <p className="mt-6 flex items-center justify-center gap-2 text-xs text-ink-tertiary">
              <ShieldCheck size={14} className="shrink-0 text-accent" />
              Free to download - no account required.
            </p>
          </div>

          <PhoneMockup
            screen={SCREENS[0]}
            // Above the fold, so the capture is not deferred.
            priority
          />
        </Reveal>

        {/* Project Card */}
        <Reveal>
          <ProjectCard />
        </Reveal>

        {/* Crafted by Athletes & Professionals */}
        <Reveal
          as="section"
          className="mx-auto mt-20 w-full max-w-6xl px-5 pb-20 sm:px-8"
        >
          <div className="grid items-center gap-10 md:grid-cols-2 md:gap-14">
            <div>
              <span className="text-[11px] font-black uppercase tracking-[0.25em] text-accent">
                Meet the team
              </span>
              {/* Left-aligned rather than SectionHeading, which centres itself
                  and would fight the two-column split. Same type ramp. */}
              <h2 className="mt-3 font-bebas text-4xl tracking-wider text-ink-primary sm:text-5xl">
                Build carefully by real athletes and professionals
              </h2>
              <p className="mt-4 leading-relaxed text-ink-secondary">
                Every program, exercise and demo in 30 Day Fitness goes through
                the same thing our members do: real sets, real fatigue and real
                form checks. Nothing goes live until it holds up under a barbell.
              </p>

              <ul className="mt-7 space-y-3">
                {CRAFTED_POINTS.map((point) => (
                  <li
                    key={point}
                    className="flex gap-3 text-sm leading-relaxed text-ink-secondary"
                  >
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative">
              {/* Gold spill echoing the accent used elsewhere, blurred so it
                  reads as light coming off the frame. */}
              <div
                aria-hidden="true"
                className="absolute -inset-6 rounded-[2.5rem] bg-accent/10 blur-3xl"
              />
              <img
                src={`${process.env.PUBLIC_URL}/meetthedeveloper.jpg`}
                alt="An athlete driving a barbell overhead in a gym, shoulder press."
                loading="lazy"
                decoding="async"
                className="relative w-full rounded-3xl border border-white/10 object-cover shadow-[0_40px_80px_-35px_rgba(0,0,0,0.9)]"
              />
            </div>
          </div>
        </Reveal>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 bg-app-bg px-6 py-8 text-sm text-ink-tertiary">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 md:flex-row">
          <p>
            &copy; {new Date().getFullYear()} 30 Day Fitness. All rights
            reserved.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <a
              href={PLAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="transition hover:text-accent"
            >
              Google Play
            </a>
            <Link to="/privacy-policy" className="transition hover:text-accent">
              Privacy Policy
            </Link>
            <Link to="/terms-of-service" className="transition hover:text-accent">
              Terms of Service
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default DayFitness;
